import { EyeOutlined } from "@ant-design/icons"
import { Button, Tooltip } from "antd"
import { AnimatePresence } from "motion/react"
import { forwardRef, useMemo, useState } from "react"

import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import Flex from "#blocks/Flex"
import TextOverrun from "#blocks/TextOverrun"
import AttendanceStatusCycleButton from "#components/AttendanceStatusCycleButton"
import { ATTENDANCE_STATUSES } from "#constants/globalConstants"
import { useFirebaseContext } from "#contexts/FirebaseContextProvider"
import { UserConstructor } from "#customTypes/composedTypes/userTypes"
import { AttendanceClassSession, AttendanceStatus } from "#customTypes/types"
import { getSessionInfoFromId } from "#utils/classHelpers"
import { STANDARD_SESSION_DATE_FORMAT } from "#utils/dateHelpers"

import { TeacherClassAttendance } from "./useTeacherAttendanceData"

const COLUMN_GAP = 0
const ROW_GAP = 8
const BOX_WIDTH = 40

const TeacherAttendanceListClassSession = forwardRef<
  HTMLDivElement,
  {
    sessionId: string
    status: AttendanceStatus
    onStatusChange: (newStatus: AttendanceStatus) => void
    sessionDate: string
  }
>(({ sessionId, status, onStatusChange, sessionDate }, ref) => {
  return (
    <AnimateInFlex
      ref={ref}
      animation={ANIMATION_BY_KEY.leftSm}
      style={{ width: BOX_WIDTH }}
      animationKey={`teacher-attendance-${sessionId}`}
    >
      <Flex col style={{ fontSize: 10 }} center>
        <p style={{ marginBottom: -4 }}>{sessionDate}</p>
        <AttendanceStatusCycleButton
          status={status}
          onStatusChange={onStatusChange}
          size="small"
        />
      </Flex>
    </AnimateInFlex>
  )
})

TeacherAttendanceListClassSession.displayName =
  "TeacherAttendanceListClassSession"

const TeacherAttendanceListClass = ({
  classAttendance,
  user,
}: {
  classAttendance: TeacherClassAttendance
  user: UserConstructor
}) => {
  const { onTeacherAttendanceChange } = useFirebaseContext()
  const [showAllSessions, setShowAllSessions] = useState(false)
  const { classId, isSummerCamp, name, attendanceSessions } = classAttendance

  // Group sessions by month for regular classes or by week for summer camps
  const groupedSessions = useMemo(() => {
    const groups: (AttendanceClassSession & {
      sessionDate: string
    })[][] = []

    const filteredSessions = showAllSessions
      ? attendanceSessions
      : attendanceSessions.filter(
          ({ status }) => status !== ATTENDANCE_STATUSES.NOT_ENROLLED
        )

    let lastSessionGroup: string | null = null
    filteredSessions.forEach((attendanceSession) => {
      const { sessionDate } = attendanceSession

      const sessionGroup =
        isSummerCamp || !showAllSessions
          ? sessionDate.format("YYYY")
          : sessionDate.format("YYYY-MM")

      if (
        lastSessionGroup !== sessionGroup ||
        groups[groups.length - 1].length === 5
      ) {
        groups.push([])
        lastSessionGroup = sessionGroup
      }
      groups[groups.length - 1].push({
        ...attendanceSession,
        sessionDate: sessionDate.format(STANDARD_SESSION_DATE_FORMAT),
      })
    })

    return groups
  }, [attendanceSessions, isSummerCamp, showAllSessions])

  return (
    <AnimateInFlex
      animation={ANIMATION_BY_KEY.leftSm}
      animationKey={`teacher-attendance-${classAttendance.classId}`}
      wait={200}
      col
      style={{
        width: 200,
        marginBottom: 30,
        breakInside: "avoid",
      }}
    >
      <h4
        style={{
          marginBottom: 4,
          width: 186,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Flex
          style={{
            minWidth: 0,
            flexGrow: 1,
          }}
        >
          <TextOverrun text={name} />
        </Flex>
        <Flex
          gap={0}
          style={{ alignItems: "center", justifyContent: "flex-end" }}
        >
          <Tooltip
            title={showAllSessions ? "Hide all sessions" : "Show all sessions"}
          >
            <Button
              icon={<EyeOutlined />}
              onClick={() => setShowAllSessions(!showAllSessions)}
              shape="circle"
              size="small"
              color={showAllSessions ? "default" : "primary"}
              variant="text"
            />
          </Tooltip>
        </Flex>
      </h4>

      <AnimateInFlex
        animation={ANIMATION_BY_KEY.leftSm}
        animationKey={`teacher-attendance-sessions-container-${classId}`}
        col
        gap={ROW_GAP}
      >
        {groupedSessions.map((sessions, groupIndex) => {
          return (
            <Flex
              gap={COLUMN_GAP}
              key={`${classId}-group-${groupIndex}-${showAllSessions}`}
              style={{
                paddingLeft: 0,
                margin: 0,
                flexWrap: showAllSessions ? "nowrap" : "wrap",
              }}
            >
              <AnimatePresence mode="popLayout">
                {sessions.map(({ status, sessionId, sessionDate }) => {
                  return (
                    <TeacherAttendanceListClassSession
                      key={`teacher-attendance-${sessionId}`}
                      sessionId={sessionId}
                      status={status}
                      sessionDate={sessionDate}
                      onStatusChange={(newStatus: AttendanceStatus) => {
                        const [classId, sessionIndex] =
                          getSessionInfoFromId(sessionId)
                        onTeacherAttendanceChange(
                          user,
                          classId,
                          sessionIndex,
                          newStatus
                        )
                      }}
                    />
                  )
                })}
              </AnimatePresence>
            </Flex>
          )
        })}
      </AnimateInFlex>
    </AnimateInFlex>
  )
}

export default TeacherAttendanceListClass
